/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';

import { getContext, IOLContext, lineStyle } from 'sdi/map/style';
import { DIV, SPAN, IMG } from 'sdi/components/elements';
import { fromRecord } from 'sdi/locale';
import {
    ILayerInfo,
    LineStyleConfig,
    LineStyleConfigSimple,
    LineStyleConfigDiscrete,
    LineStyleConfigContinuous,
} from 'sdi/source';

import { hasZoomConstraint, getItemZoom } from './legend-item';
import { uniqId } from 'sdi/util';
import { applyResolutionStyle } from 'sdi/components/legend';

const logger = debug('sdi:legend-linestring');

const lineGeometry = new LineString([
    [0, 15],
    [32, 15],
]);

const item = (
    geomType: string,
    dataUrl: string,
    label: string,
    zoomConstraint: boolean,
) => {
    return DIV(
        { className: `legend-item ${geomType}`, key: uniqId() },
        DIV({ className: 'item-style' }, IMG({ src: dataUrl, alt: geomType })),
        DIV({ className: 'item-label' }, SPAN({}, label)),
        getItemZoom(zoomConstraint),
    );
};

const renderSimple = (
    config: LineStyleConfigSimple,
    layerInfo: ILayerInfo,
    ctx: IOLContext,
) => {
    const { canvas, olContext } = ctx;
    const styles = applyResolutionStyle(
        lineStyle(config),
        new Feature(lineGeometry),
    );
    styles.forEach(style => {
        olContext.setStyle(style);
        olContext.drawGeometry(lineGeometry);
    });

    return [item('line', canvas.toDataURL(), '', hasZoomConstraint(layerInfo))];
};

const renderDiscrete = (
    config: LineStyleConfigDiscrete,
    _layerInfo: ILayerInfo,
    ctx: IOLContext,
) => {
    const { canvas, canvasContext, olContext } = ctx;
    const styleFn = lineStyle(config);
    const items: React.DOMElement<{}, Element>[] = [];
    config.groups.forEach(group => {
        if (group.values.length > 0) {
            canvasContext.clearRect(0, 0, 100, 100);
            const f = new Feature(lineGeometry);
            f.set(config.propName, group.values[0]);
            const styles = applyResolutionStyle(styleFn, f);
            styles.forEach(style => {
                olContext.drawFeature(f, style);
            });
            items.push(
                item(
                    'line',
                    canvas.toDataURL(),
                    fromRecord(group.label),
                    hasZoomConstraint(_layerInfo),
                ),
            );
        }
    });

    return items;
};

const renderContinuous = (
    config: LineStyleConfigContinuous,
    _layerInfo: ILayerInfo,
    ctx: IOLContext,
) => {
    const { canvas, canvasContext, olContext } = ctx;
    const styleFn = lineStyle(config);
    const items: React.DOMElement<{}, Element>[] = [];
    config.intervals.forEach(interval => {
        canvasContext.clearRect(0, 0, 100, 100);
        const f = new Feature(lineGeometry);
        const v = interval.low + (interval.high - interval.low) / 2;
        f.set(config.propName, v);
        const styles = applyResolutionStyle(styleFn, f);
        styles.forEach(style => {
            olContext.drawFeature(f, style);
        });
        items.push(
            item(
                'line',
                canvas.toDataURL(),
                fromRecord(interval.label),
                hasZoomConstraint(_layerInfo),
            ),
        );
    });

    return items;
};

const render = (config: LineStyleConfig, layerInfo: ILayerInfo) => {
    const ctx = getContext(20, 32);
    if (ctx) {
        switch (config.kind) {
            case 'line-simple':
                return renderSimple(config, layerInfo, ctx);
            case 'line-discrete':
                return renderDiscrete(config, layerInfo, ctx);
            case 'line-continuous':
                return renderContinuous(config, layerInfo, ctx);
        }
    }

    return [];
};

export default render;

logger('loaded');
